import { useCallback } from "react";
import { axiosInstance } from "./useApi";

/**
 * Hook for tracking and logging errors to the backend
 * @returns {Object} - Error tracking functions
 */
const useErrorTracking = () => {
  /**
   * Log an error to the backend
   * @param {Error|string} error - The error object or message
   * @param {Object} context - Additional context about the error
   * @param {string} severity - Error severity: 'low', 'medium', 'high', 'critical'
   * @param {string} category - Error category (optional)
   */
  const logError = useCallback(
    async (error, context = {}, severity = "medium", category = null) => {
      try {
        // Handle both Error objects and string messages
        const errorMessage =
          typeof error === "string" ? error : error?.message || "Unknown error";
        const stackTrace = typeof error === "string" ? "" : error?.stack || "";

        const errorData = {
          message: errorMessage,
          stackTrace,
          severity,
          category: category || "web",
          details: {
            context,
            errorName: error?.name || "Error",
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            ...context,
          },
        };

        const token = localStorage.getItem("token");
        const headers = {
          action: "createErrorLog",
          entityName: "Log",
        };

        if (token) {
          headers.authorization = `Bearer ${token}`;
        }

        const response = await axiosInstance.post(
          "/",
          { data: JSON.stringify(errorData) },
          { headers },
        );

        return response.data;
      } catch (err) {
        // Silent fail - don't want error logging to crash the app
        console.error("Failed to log error:", err);
        return null;
      }
    },
    [],
  );

  /**
   * Log a handled error (caught exception that was handled gracefully)
   */
  const logHandledError = useCallback(
    async (error, context = {}) => {
      return logError(error, { ...context, handled: true }, "low");
    },
    [logError],
  );

  /**
   * Log a critical error (app-breaking issues)
   */
  const logCriticalError = useCallback(
    async (error, context = {}) => {
      return logError(error, context, "critical");
    },
    [logError],
  );

  /**
   * Log a network error
   */
  const logNetworkError = useCallback(
    async (error, endpoint, context = {}, options = {}) => {
      const severity = options?.severity || "medium";
      const category = options?.category || "network";
      const type = options?.type || "network";

      return logError(
        error,
        {
          ...context,
          endpoint,
          type,
        },
        severity,
        category,
      );
    },
    [logError],
  );

  /**
   * Log a data error (invalid data, validation errors)
   */
  const logDataError = useCallback(
    async (error, component, context = {}) => {
      return logError(
        error,
        {
          ...context,
          component,
          type: "data",
        },
        "medium",
        "data",
      );
    },
    [logError],
  );

  return {
    logError,
    logHandledError,
    logCriticalError,
    logNetworkError,
    logDataError,
  };
};

export default useErrorTracking;
